import { useEffect, useState } from "react";
import { api, formatIDR } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { TrendingUp, Truck, Container } from "lucide-react";
import { Link } from "react-router-dom";

const YEARS = (() => {
  const now = new Date().getFullYear();
  return [now, now - 1, now - 2];
})();

export default function VehicleReport() {
  const [trucks, setTrucks] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [selected, setSelected] = useState(""); // "truck:id" or "trailer:id"
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [detail, setDetail] = useState(null);
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get("/trucks").then((r) => setTrucks(r.data)).catch(() => {});
    api.get("/trailers").then((r) => setTrailers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/reports/all-vehicles", { params: year && year !== "all" ? { year } : {} })
      .then((r) => setOverview(r.data))
      .catch(() => {});
  }, [year]);

  useEffect(() => {
    if (!selected) { setDetail(null); return; }
    const [vt, vid] = selected.split(":");
    api.get(`/reports/vehicle/${vt}/${vid}`, { params: year && year !== "all" ? { year } : {} })
      .then((r) => setDetail(r.data))
      .catch(() => setDetail(null));
  }, [selected, year]);

  return (
    <div>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-slate-900">Laporan Biaya per Unit</h1>
          <p className="text-slate-500 text-sm mt-1">Rincian biaya ban & sparepart per truck / trailer.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Tahun</label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-11 rounded-sm w-32" data-testid="report-year"><SelectValue /></SelectTrigger>
              <SelectContent>
                {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                <SelectItem value="all">Semua</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500 block mb-1">Pilih Unit</label>
            <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger className="h-11 rounded-sm w-64" data-testid="report-vehicle">
                <SelectValue placeholder="-- Pilih truck / trailer --" />
              </SelectTrigger>
              <SelectContent>
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-500">Truck</div>
                {trucks.map((t) => <SelectItem key={t.id} value={`truck:${t.id}`}>{t.plat_nomor} — {t.brand} {t.model}</SelectItem>)}
                <div className="px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-slate-500 border-t mt-1">Trailer</div>
                {trailers.map((t) => <SelectItem key={t.id} value={`trailer:${t.id}`}>{t.code} — {t.size}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {!selected ? (
        <OverviewTable overview={overview} year={year} />
      ) : (
        detail && <VehicleDetailReport data={detail} year={year} />
      )}
    </div>
  );
}

function OverviewTable({ overview, year }) {
  if (!overview) return <div className="text-slate-500">Memuat...</div>;
  const grand = overview.rows.reduce((a, b) => a + b.total, 0);
  return (
    <div>
      <div className="mb-4 border-2 border-slate-900 bg-slate-900 text-white p-5 rounded-sm">
        <div className="text-xs uppercase tracking-widest text-orange-400 font-bold">
          Total Biaya Armada {year && year !== "all" ? year : "(Semua Periode)"}
        </div>
        <div className="font-display text-4xl font-black mt-2" data-testid="overview-grand-total">{formatIDR(grand)}</div>
        <div className="text-xs text-slate-400 mt-1">{overview.rows.length} unit dengan biaya tercatat</div>
      </div>

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-900 hover:bg-slate-900">
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Unit</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold">Tipe</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Ban</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Sparepart</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Total</TableHead>
              <TableHead className="text-orange-400 uppercase text-xs tracking-widest font-bold text-right">Jumlah Entry</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {overview.rows.map((r) => (
              <TableRow key={`${r.vehicle_type}-${r.vehicle_id}`} data-testid={`overview-row-${r.vehicle_label}`}>
                <TableCell>
                  <Link to={`/vehicles/${r.vehicle_type}/${r.vehicle_id}`} className="font-mono font-bold text-slate-900 hover:text-orange-600">
                    {r.vehicle_label}
                  </Link>
                </TableCell>
                <TableCell>
                  <span className="text-xs uppercase tracking-widest font-bold flex items-center gap-1">
                    {r.vehicle_type === "truck" ? <Truck className="w-3 h-3" /> : <Container className="w-3 h-3" />}
                    {r.vehicle_type}
                  </span>
                </TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.ban)}</TableCell>
                <TableCell className="text-right font-mono">{formatIDR(r.sparepart)}</TableCell>
                <TableCell className="text-right font-mono font-bold text-slate-900">{formatIDR(r.total)}</TableCell>
                <TableCell className="text-right text-slate-500 text-sm">{r.count}</TableCell>
              </TableRow>
            ))}
            {overview.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-10 text-slate-500">Belum ada data biaya untuk periode ini.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function VehicleDetailReport({ data, year }) {
  const { vehicle, vehicle_type, totals, monthly, yearly, expenses } = data;
  const identifier = vehicle_type === "truck" ? vehicle.plat_nomor : vehicle.code;
  return (
    <div className="space-y-6">
      <div className="border-2 border-slate-900 bg-white p-5 rounded-sm flex items-center gap-4">
        <div className="w-12 h-12 bg-slate-900 flex items-center justify-center text-orange-400">
          {vehicle_type === "truck" ? <Truck className="w-6 h-6" /> : <Container className="w-6 h-6" />}
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{vehicle_type === "truck" ? "Head Truck" : "Trailer"} · Laporan {year && year !== "all" ? year : "(Semua Periode)"}</div>
          <div className="font-mono font-black text-3xl text-slate-900" data-testid="detail-identifier">{identifier}</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SumCard label="Total Ban" value={formatIDR(totals.ban)} accent="bg-slate-900 text-orange-400" testId="detail-total-ban" />
        <SumCard label="Total Sparepart" value={formatIDR(totals.sparepart)} accent="bg-emerald-600 text-white" testId="detail-total-sparepart" />
        <SumCard label="Grand Total" value={formatIDR(totals.grand)} accent="bg-orange-500 text-slate-900" testId="detail-grand-total" />
      </div>

      <div className="border-2 border-slate-200 bg-white p-5 rounded-sm">
        <div className="font-display font-black text-lg text-slate-900 mb-3">Biaya per Bulan</div>
        <div className="h-64">
          {monthly.length === 0 ? (
            <div className="h-full flex items-center justify-center text-slate-400">Belum ada data</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: 11 }} />
                <YAxis stroke="#64748b" style={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1e6).toFixed(1)}jt`} />
                <Tooltip formatter={(v) => formatIDR(v)} />
                <Legend />
                <Bar dataKey="ban" stackId="a" fill="#ea580c" name="Ban" />
                <Bar dataKey="sparepart" stackId="a" fill="#059669" name="Sparepart" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {yearly.length > 0 && (
        <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
            <div className="font-display font-black text-lg text-slate-900">Rekap Tahunan</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="uppercase text-xs tracking-widest font-bold">Tahun</TableHead>
                <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Ban</TableHead>
                <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Sparepart</TableHead>
                <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {yearly.map((y) => (
                <TableRow key={y.year}>
                  <TableCell className="font-mono font-bold">{y.year}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(y.ban)}</TableCell>
                  <TableCell className="text-right font-mono">{formatIDR(y.sparepart)}</TableCell>
                  <TableCell className="text-right font-mono font-bold">{formatIDR(y.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="border-2 border-slate-200 bg-white rounded-sm overflow-hidden">
        <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
          <div className="font-display font-black text-lg text-slate-900">Detail Transaksi</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Tanggal</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Kategori</TableHead>
              <TableHead className="uppercase text-xs tracking-widest font-bold">Deskripsi</TableHead>
              {vehicle_type === "truck" && <TableHead className="uppercase text-xs tracking-widest font-bold">KM</TableHead>}
              <TableHead className="uppercase text-xs tracking-widest font-bold text-right">Biaya</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((e) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-sm">{e.event_date}</TableCell>
                <TableCell className="text-xs uppercase font-bold">{e.category}</TableCell>
                <TableCell className="text-sm">{e.description}</TableCell>
                {vehicle_type === "truck" && <TableCell className="font-mono text-sm">{e.km ? `${e.km.toLocaleString()}` : "-"}</TableCell>}
                <TableCell className="text-right font-mono">{formatIDR(e.amount)}</TableCell>
              </TableRow>
            ))}
            {expenses.length === 0 && (
              <TableRow>
                <TableCell colSpan={vehicle_type === "truck" ? 5 : 4} className="text-center py-8 text-slate-500 text-sm">Belum ada transaksi.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function SumCard({ label, value, accent, testId }) {
  return (
    <div className="border-2 border-slate-200 bg-white p-4 rounded-sm" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</div>
      <div className={`inline-block px-2 py-1 mt-2 rounded-sm font-display font-black text-xl ${accent}`}>{value}</div>
    </div>
  );
}
